import c from 'classnames';
import { useMemo, useState } from 'react';
import { Typography } from '../Typography';
import TextEditorFooter from './components/TextEditorFooter';
import { TextEditorHandler } from './handlers/textEditorHandler';
import useIsEditorEmpty from './hooks/useIsEditorEmpty';
import useLastSavedAt from './hooks/useLastSavedAt';
import {
  $getRoot,
  ContentEditable,
  EditorState,
  HistoryPlugin,
  LexicalComposer,
  LexicalErrorBoundary,
  LinkPlugin,
  ListPlugin,
  OnChangePlugin,
  RichTextPlugin,
} from './lexical-imports';
import OnDirtyPlugin from './plugins/OnDirtyPlugin';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';
import styles from './TextEditor.module.css';
import { TextEditorState } from './types';
import { debouncedOnChangeHandler } from './utils/utils';

type Props = {
  containerClassName?: string;
  contentEditableClassName?: string;
  placeholder?: string;
  editorStateLocalStorageKey: keyof TextEditorState;
  onSubmit?: (editorState: string) => void | Promise<void>;
  onAutoSave?: () => void;
  onRevert?: () => void;
  initialValue?: string;
  disabled?: boolean;
  isLoading?: boolean;
  fullHeight?: boolean;
  buttonFullWidth?: boolean;
};

const textEditorHandler = TextEditorHandler.instance;

export function TextEditor({
  containerClassName,
  contentEditableClassName,
  placeholder = 'Enter content...',
  editorStateLocalStorageKey,
  onSubmit,
  onAutoSave,
  onRevert,
  initialValue,
  disabled,
  isLoading,
  fullHeight = false,
  buttonFullWidth = false,
}: Props) {
  const textEditorInitialConfig = useMemo(
    () =>
      textEditorHandler.getTextEditorInitialConfig(
        editorStateLocalStorageKey,
        initialValue,
      ),
    [editorStateLocalStorageKey, initialValue],
  );

  const [isEmpty, setIsEmpty] = useIsEditorEmpty({
    editorState: textEditorInitialConfig.editorState,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useLastSavedAt(
    editorStateLocalStorageKey,
  );

  function handleChange(editorState: EditorState) {
    editorState.read(() => {
      const textContent = $getRoot().getTextContent();
      const isEmpty = textContent.trim().length === 0;
      setIsEmpty(isEmpty);

      debouncedOnChangeHandler(
        editorState,
        editorStateLocalStorageKey,
        setLastSavedAt,
        isEmpty,
        onAutoSave,
      );
    });
  }

  return (
    <LexicalComposer initialConfig={textEditorInitialConfig}>
      <div
        className={c(
          styles.container,
          fullHeight && styles.containerFullHeight,
          containerClassName,
        )}
      >
        <ToolbarPlugin />
        <div
          className={c(
            styles.editorContainer,
            fullHeight && styles.editorContainerFullHeight,
          )}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={c(
                  styles.contentEditable,
                  fullHeight && styles.contentEditableFullHeight,
                  contentEditableClassName,
                )}
              />
            }
            placeholder={
              <Typography variant='body-s' className={styles.placeholder}>
                {placeholder}
              </Typography>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
          <OnDirtyPlugin
            onDirtyChange={setIsDirty}
            initialValue={initialValue}
          />
        </div>
      </div>
      <TextEditorFooter
        initialValue={initialValue}
        lastSavedAt={lastSavedAt}
        isDirty={isDirty}
        isLoading={isLoading}
        disabled={
          disabled || isLoading || isEmpty || (!isDirty && !!initialValue)
        }
        onSubmit={() =>
          textEditorHandler.handleSubmit(editorStateLocalStorageKey, onSubmit)
        }
        onRevert={onRevert}
        buttonFullWidth={buttonFullWidth}
      />
    </LexicalComposer>
  );
}
